import { useEffect, useState, type ReactNode } from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShortTextIcon from '@mui/icons-material/ShortText';
import { useAIActions } from '../../api/queries/aiActions';
import { AIActionDialog } from './AIActionDialog';
import { useShortcut } from '../../lib/keybindings';

interface QuickCommand {
  command: string;
  label: string;
  description: string;
  icon: ReactNode;
  shortcut?: string;
  callbackKey: keyof Pick<
    SlashCommandMenuProps,
    | 'onImageGenerate'
    | 'onAudioGenerate'
    | 'onExpand'
    | 'onRewrite'
    | 'onContinue'
    | 'onSummarize'
  >;
}

const QUICK_COMMANDS: QuickCommand[] = [
  {
    command: '/image',
    label: 'Generate image for this paragraph',
    description: 'Create an AI-generated illustration',
    icon: <ImageIcon fontSize="small" />,
    shortcut: '\u2318R',
    callbackKey: 'onImageGenerate',
  },
  {
    command: '/audio',
    label: 'Generate narration',
    description: 'Create AI voice narration',
    icon: <AudiotrackIcon fontSize="small" />,
    shortcut: undefined,
    callbackKey: 'onAudioGenerate',
  },
  {
    command: '/expand',
    label: 'Expand this paragraph',
    description: 'Add detail and depth to the text',
    icon: <AutoFixHighIcon fontSize="small" />,
    shortcut: undefined,
    callbackKey: 'onExpand',
  },
  {
    command: '/rewrite',
    label: 'Rewrite this paragraph',
    description: 'Rephrase with a different style',
    icon: <EditNoteIcon fontSize="small" />,
    shortcut: undefined,
    callbackKey: 'onRewrite',
  },
  {
    command: '/continue',
    label: 'Continue writing',
    description: 'Continue from where the paragraph ends',
    icon: <ArrowForwardIcon fontSize="small" />,
    shortcut: undefined,
    callbackKey: 'onContinue',
  },
  {
    command: '/summarize',
    label: 'Summarize selection',
    description: 'Create a concise summary',
    icon: <ShortTextIcon fontSize="small" />,
    shortcut: undefined,
    callbackKey: 'onSummarize',
  },
];

export interface SlashCommandMenuProps {
  paragraphId?: string | null;
  bookId?: string;
  chapterId?: string;
  contextParams?: Record<string, unknown>;
  onImageGenerate?: () => void;
  onAudioGenerate?: () => void;
  onTextGenerate?: () => void;
  onExpand?: () => void;
  onRewrite?: () => void;
  onContinue?: () => void;
  onSummarize?: () => void;
  /** Render target for the menu positioning -- typically the editor root. */
  children: ReactNode;
}

/**
 * Cmd+/ opens a slash-command menu anchored to the current selection (falls back to
 * viewport center). Typing filters the list; Enter selects. Shows hardcoded quick
 * commands followed by registry actions separated by a divider.
 */
export function SlashCommandMenu({
  contextParams,
  children,
  onImageGenerate,
  onAudioGenerate,
  onExpand,
  onRewrite,
  onContinue,
  onSummarize,
}: SlashCommandMenuProps) {
  const { data: actions = [] } = useAIActions();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [dialogSlug, setDialogSlug] = useState<string | null>(null);

  useShortcut({ key: '/', meta: true }, (event) => {
    event.preventDefault();
    const sel = window.getSelection();
    const range =
      sel && sel.rangeCount > 0
        ? sel.getRangeAt(0).getBoundingClientRect()
        : null;
    setPosition(
      range
        ? { x: range.right, y: range.bottom }
        : { x: window.innerWidth / 2, y: window.innerHeight / 3 },
    );
    setFilter('');
    setOpen(true);
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
      else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
        setFilter((f) => (f + e.key).toLowerCase());
      } else if (e.key === 'Backspace') {
        setFilter((f) => f.slice(0, -1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const callbackMap: Record<string, (() => void) | undefined> = {
    onImageGenerate,
    onAudioGenerate,
    onExpand,
    onRewrite,
    onContinue,
    onSummarize,
  };

  // Filter quick commands by search
  const filteredQuickCommands = QUICK_COMMANDS.filter(
    (cmd) =>
      cmd.command.toLowerCase().includes(filter) ||
      cmd.label.toLowerCase().includes(filter),
  );

  // Filter registry actions by search
  const filteredActions = actions.filter((a) =>
    a.name.toLowerCase().includes(filter),
  );

  const hasQuickResults = filteredQuickCommands.length > 0;
  const hasRegistryResults = filteredActions.length > 0;
  const selected = actions.find((a) => a.slug === dialogSlug) ?? null;

  return (
    <>
      {children}
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorReference="anchorPosition"
        anchorPosition={
          position ? { top: position.y, left: position.x } : undefined
        }
        slotProps={{
          paper: {
            sx: { minWidth: 320, maxHeight: 400 },
          },
        }}
      >
        {/* Quick commands section */}
        {filteredQuickCommands.map((cmd) => (
          <MenuItem
            key={cmd.command}
            onClick={() => {
              setOpen(false);
              callbackMap[cmd.callbackKey]?.();
            }}
            sx={{ py: 1 }}
          >
            <ListItemIcon>{cmd.icon}</ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {cmd.command}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {cmd.label}
                  </Typography>
                </Typography>
              }
              secondary={cmd.description}
            />
            {cmd.shortcut && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 2, flexShrink: 0, fontFamily: 'monospace' }}
              >
                {cmd.shortcut}
              </Typography>
            )}
          </MenuItem>
        ))}

        {/* Divider between quick commands and registry actions */}
        {hasQuickResults && hasRegistryResults && <Divider />}

        {/* Registry actions section */}
        {filteredActions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => {
              setOpen(false);
              setDialogSlug(action.slug);
            }}
            sx={{ py: 1 }}
          >
            <ListItemText
              primary={action.name}
              secondary={action.description ?? action.slug}
            />
          </MenuItem>
        ))}

        {/* Empty state */}
        {!hasQuickResults && !hasRegistryResults && (
          <MenuItem disabled>No matching actions</MenuItem>
        )}
      </Menu>

      <AIActionDialog
        open={selected !== null}
        onClose={() => setDialogSlug(null)}
        action={selected}
        contextParams={contextParams}
      />
    </>
  );
}
